import { FC, useMemo, useState } from 'react';
import { FieldArray, getIn } from 'formik';
// Components
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import {
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  DndContext,
  closestCenter,
  DragOverlay
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { getOrdinalNumber } from '../../../../../helpers';
import { theme } from '../../../../../styles/theme';
import { OptionListItem } from './option-list-item';
import { StaticOptionListItem } from './static-option-list-item';
import { useQuery } from 'react-query';
import { getFieldWalkthroughLocationManagementLocations } from '../../../../../fetch/lookups/field-walkthrough-location-management';
import { getFieldWalkthroughManagementLocationItems } from '../../../../../fetch/field-walkthrough-location-management';

interface IListManagementDetailsDragAndDrop {
  setFieldValue: (key: string, val: any) => void;
  values: any[];
  touched: any;
  errors: any;
  handleChange: any;
  handleBlur: any;
  handleSubmit: () => void;
  isSubmitting: boolean;
  dirty: boolean;
  isValid: boolean;
}

export const ListManagementDetailsDragAndDrop: FC<IListManagementDetailsDragAndDrop> = ({
  setFieldValue,
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  dirty,
  isValid,
  handleSubmit,
  isSubmitting
}) => {
  const classes = useStyles({ isOptionsScroll: values?.length > 4 });

  const [activeId, setActiveId] = useState<any>();
  const items = useMemo(() => values?.map(({ templateItemId }: any) => templateItemId), [values]);
  // Use the state and functions returned from useTable to build your UI

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newTemplateItems = arrayMove(values, oldIndex, newIndex);
      setFieldValue('templateItems', newTemplateItems);
    }

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const selectedRow = useMemo(() => {
    if (!activeId) {
      return null;
    }
    //@ts-ignore
    const row = values.find(option => option?.templateItemId === activeId) as Row<any>;
    return row;
  }, [activeId, values]);

  const [selectedImportLocation, setSelectedImportLocation] = useState(null);
  const { isLoading: isLoadingImportLocations, data: importLocations } = useQuery(
    ['importLocations'],
    // @Genevieve: I'm not sure if this is the correct function to call here. I think since the we removed the filter on this page
    // we should be returning all locations, active or inactive. I'm leaving it as active for now, following a similar pattern to
    // the other queries in these related components.
    async () => await getFieldWalkthroughLocationManagementLocations(true),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingImportLocationItems, data: importLocationItems } = useQuery(
    ['importLocations', selectedImportLocation],
    // @Genevieve: I'm not sure if this is the correct function to call here. I think since the we removed the filter on this page
    // we should be returning all locations, active or inactive. I'm leaving it as active for now, following a similar pattern to
    // the other queries in these related components.
    async () => await getFieldWalkthroughManagementLocationItems(selectedImportLocation),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!selectedImportLocation
    }
  );

  const handleImport = (push: (obj: any) => void) => {
    importLocationItems.forEach((itemText: any, index: number) => {
      push({
        templateItemId: 'new-' + new Date().getTime() + index,
        text: itemText,
        isActive: true
      });
    });
  };

  return (
    <Grid className={classes.grid} container spacing={1}>
      {
        <Grid item xs={12} className={classes.optionsContainer}>
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext items={items ?? []} strategy={verticalListSortingStrategy}>
              <FieldArray name="templateItems">
                {({ push }) => (
                  <>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel id="importFrom">Copy From</InputLabel>
                          <Select
                            name="importFrom"
                            labelId="importFrom"
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={selectedImportLocation}
                            onChange={event => {
                              let target = event.target as HTMLInputElement;
                              const { value } = target;
                              setSelectedImportLocation(value);
                            }}
                          >
                            <MenuItem key={`none`} value={null}>
                              Select Location...
                            </MenuItem>
                            {!isLoadingImportLocations
                              ? importLocations?.map((option, index) => (
                                  <MenuItem key={`${option?.value}`} value={option?.value}>
                                    {option?.description}
                                  </MenuItem>
                                ))
                              : []}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          type="button"
                          color="secondary"
                          variant="contained"
                          disabled={!selectedImportLocation || isLoadingImportLocationItems}
                          onClick={() => handleImport(push)}
                        >
                          Import
                        </Button>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          type="button"
                          color="secondary"
                          variant="contained"
                          onClick={() =>
                            push({
                              templateItemId: 'new-' + new Date().getTime(),
                              text: '',
                              isActive: true
                            })
                          }
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                    <div className={classes.list}>
                      {values?.map((option: any, index: number) => {
                        const text = `templateItems[${index}].text`;
                        const touchedText = getIn(touched, text);
                        const errorText = getIn(errors, text);
                        const isActive = `templateItems[${index}].isActive`;

                        return (
                          <OptionListItem
                            key={option.templateItemId}
                            item={option}
                            id={option.templateItemId}
                          >
                            <Grid container spacing={1}>
                              <Grid item xs={10}>
                                <TextField
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  classes={{ root: classes.textFieldRoot }}
                                  label={`${getOrdinalNumber(index + 1)} item`}
                                  name={text}
                                  value={option.text}
                                  required
                                  helperText={touchedText && errorText ? errorText : ''}
                                  error={Boolean(touchedText && errorText)}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  InputLabelProps={{
                                    classes: { root: classes.inputLabelRoot }
                                  }}
                                  InputProps={{
                                    classes: { root: classes.inputRoot }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <FormControlLabel
                                  color="primary"
                                  control={
                                    <Switch
                                      checked={option?.isActive}
                                      id={`${option.templateItemId}-list-item-switch`}
                                      onChange={handleChange}
                                      name={isActive}
                                    />
                                  }
                                  label="Active"
                                />
                              </Grid>
                            </Grid>
                          </OptionListItem>
                        );
                      })}
                    </div>
                  </>
                )}
              </FieldArray>
            </SortableContext>
            <DragOverlay>
              {activeId && (
                <StaticOptionListItem item={selectedRow} id={selectedRow?.templateItemId}>
                  {selectedRow?.text}
                </StaticOptionListItem>
              )}
            </DragOverlay>
          </DndContext>
        </Grid>
      }
    </Grid>
  );
};

const useStyles = makeStyles<Theme, { isOptionsScroll: boolean }>(() => ({
  emphasis: {
    fontWeight: 600,
    color: theme.palette.secondary.main
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  list: {
    paddingRight: ({ isOptionsScroll }) =>
      isOptionsScroll ? theme.spacing(0.5) : theme.spacing(0),
    margin: theme.spacing(0.5, 0, 1, 0),
    overflowY: 'auto'
  },
  grid: {
    marginTop: theme.spacing(0.5),
    paddingTop: '0 !important'
  },
  inputRoot: {
    fontSize: '1rem'
  },
  inputLabelRoot: {
    fontSize: '1rem'
  },
  textFieldRoot: {
    fontSize: '1rem'
  }
}));
